import { Select } from '@amzn/awsui-components-react';
import type { AllowedIndustriesStatus } from '../useAllowedIndustries';

/**
 * The industry scope for the STO view — exactly ONE industry, no "all industries" option.
 *
 * The STO view queries a single industry at a time (a product decision), so every option
 * here is a real industry code and choosing one is the only way the scope changes. There
 * used to be an ALL option that sent no `industries` member and let the service scope the
 * answer to the permitted set; it is gone on purpose, not merely hidden, and a request with
 * that shape can no longer be produced from this page.
 *
 * Presentational: the permitted set is fetched by the page (useAllowedIndustries), because
 * the page defaults the selection to its first entry and holds the STO requests until it
 * has one. Options are that set verbatim, in its order — GetAllowedIndustries, the caller's
 * OWN permitted industries, never ListIndustries — so a user is not offered an industry
 * they are not allowed to read.
 */

interface Props {
  /** The permitted set; `null` while it is still loading. */
  industries: string[] | null;
  status: AllowedIndustriesStatus;
  /** `null` only while nothing can be selected yet (loading, failed, or permitted nothing). */
  selected: string | null;
  onChange: (industry: string) => void;
}

export function IndustrySelect({ industries, status, selected, onChange }: Props) {
  const options = (industries ?? []).map((code) => ({ value: code, label: code }));

  return (
    <Select
      selectedOption={options.find((o) => o.value === selected) ?? null}
      options={options}
      onChange={({ detail }) => {
        const value = detail.selectedOption.value;
        if (value !== undefined) onChange(value);
      }}
      // Nothing to pick from, so nothing to open: the page explains the empty state itself.
      disabled={status === 'ready' && options.length === 0}
      placeholder={status === 'ready' && options.length === 0 ? '无可见行业' : '选择行业'}
      statusType={status === 'loading' ? 'loading' : status === 'failed' ? 'error' : 'finished'}
      loadingText="加载可见行业…"
      errorText="无法加载可见行业"
      ariaLabel="行业"
    />
  );
}
